import type { ReactNode } from 'react';
import { CheckCircleIcon, CircleNotchIcon, XCircleIcon } from '@phosphor-icons/react';

import type { ToolCallState } from '../../stores/agent-session-store';

import { toolMeta } from './tool-metadata';

function StatusIcon({ toolCall }: { toolCall: ToolCallState }) {
  if (toolCall.status === 'running') {
    return <CircleNotchIcon size={13} className="shrink-0 animate-spin text-fg-tertiary" />;
  }
  if (toolCall.isError) {
    return <XCircleIcon size={13} weight="fill" className="shrink-0 text-danger" />;
  }
  return <CheckCircleIcon size={13} weight="fill" className="shrink-0 text-success" />;
}

/**
 * The one-line header every tool call shows: status, the tool's icon and title, and a summary of
 * what it targeted. Shared by the generic row and the bespoke cards, so a tool reads the same
 * whichever renderer it lands on. `children` trails the summary (the staff debug toggle).
 */
export function ToolCallHeader({
  toolCall,
  summary,
  children,
}: {
  toolCall: ToolCallState;
  summary: string | null;
  children?: ReactNode;
}) {
  const meta = toolMeta(toolCall.name);
  const ToolIcon = meta.icon;

  return (
    <div className="flex items-center gap-2">
      <StatusIcon toolCall={toolCall} />
      <ToolIcon size={14} className="shrink-0 text-fg-tertiary" />
      <span className="shrink-0 text-xs font-medium text-fg">{meta.humanTitle}</span>
      {summary && (
        <code className="min-w-0 flex-1 truncate font-mono text-2xs text-fg-tertiary" title={summary}>
          {summary}
        </code>
      )}
      {children}
    </div>
  );
}
